import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useEvent } from "../context/EventProvider";
import { ArticlePrice } from "../funnel/ArticlePrice";
import { PromoCode } from "../funnel/PromoCode";
import { useAnswers } from "../funnel/answers";
import { draftSelection, useDraft } from "../funnel/draft";
import {
  toggle,
  isSelected,
  salesDays,
  eachDayChosen,
  locked,
  euros,
  flatIds,
  type SalesDay,
  type Selection,
  cleanSelection,
} from "../funnel/articles";
import { longDay, selectedDaysOf } from "../funnel/dates";
import { SaveNote, useStepSave } from "../funnel/stepSave";
import { useArticles, useResume } from "../funnel/useArticles";
import { CONFIRMATION } from "../funnel/thread";
import { useFunnelNav } from "../funnel/useFunnelNav";
import { useParticipation } from "../funnel/useParticipation";
import { fallbackOf } from "../settings/catalog";
import { useFlagOr, useLocale, useSettings, useT, useText } from "../settings/context";
import { Button } from "../ui/Button";
import { Icon } from "../ui/Icon";
import { Prose } from "../ui/Prose";
import { InPlace } from "../panel/InPlace";
import { withContext } from "./registry";

/**
 * L'ÉTAPE BILLETTERIE — ce qu'on achète, et ce que ça coûte.
 *
 * ⚠️ ELLE NE FAIT QUE COCHER : rien n'est écrit ici. La sélection part avec le reste de
 * l'inscription, à la confirmation — c'est ce que fait la V1, qui l'envoie depuis son
 * écran caché final (`screens/steps/Dates2.jsx`), et la raison est la même que pour les
 * dates : la route d'écriture DÉTRUIT la sélection existante avant de la recréer, donc
 * l'appeler en chemin détruirait la commande de quelqu'un qui abandonne à l'étape
 * suivante.
 *
 * ⚠️ MAIS ELLE EST GARDÉE EN BROUILLON DANS CE NAVIGATEUR, sans rien commander : qui
 * abandonne en route la retrouve à son retour. Voir `funnel/draft.ts`.
 *
 * ⚠️ SAUF SERVIE EN PAGE (`asPage`), hors du tunnel : « Enregistrer » écrit ici même, et
 * remplace la commande enregistrée par celle qu'on vient de composer. Dans le tunnel,
 * même en correction, la sélection suit le parcours jusqu'à la confirmation (tâche #546,
 * voir l'étape dates). Voir `funnel/stepSave.tsx`.
 *
 * ⚠️ LE TOTAL VIENT DU SERVEUR, à chaque changement. Voir `funnel/articles.ts` : les
 * gratuits, les codes de remise, la remise de société, la TVA par article et ce qui a
 * déjà été facturé sont des choses que cet écran ne peut pas connaître.
 *
 * ⚠️ ON COCHE, ON NE COMPTE PAS. Le prototype montre des molettes de quantité : le
 * produit n'en a pas — `ArticleUsers` porte une ligne par (article, jour, personne).
 * Ce qui en tient lieu, c'est le MÊME article retenu sur plusieurs jours.
 *
 * Displayed strings go through `t()`, French kept at the call site as the
 * fallback — EXCEPT the messages addressed to the ORGANISER (an empty catalog,
 * a step removed from the thread), French like the rest of the panel.
 */

const ICI = "/registration/articles";

export function Articles({ asPage }: { asPage?: true }) {
  const t = useT();
  const { days } = useSettings();
  const [answers, record] = useAnswers();
  const { me, recorded } = useParticipation();
  /* La commande composée lors d'un premier parcours abandonné (voir `funnel/draft.ts`). */
  const { draft, keep } = useDraft(me, recorded);

  const title = useText(
    "FO_MEETING_V2_ARTICLES_TITLE",
    fallbackOf("FO_MEETING_V2_ARTICLES_TITLE"),
  );
  const subtitle = useText(
    "FO_MEETING_V2_ARTICLES_SUBTITLE",
    fallbackOf("FO_MEETING_V2_ARTICLES_SUBTITLE"),
  );
  const html = useText("FO_MEETING_V2_ARTICLES_HTML");
  const required = useFlagOr("FO_MEETING_V2_ARTICLES_REQUIRED");
  const mentions = useText("FO_MEETING_V2_ARTICLES_NOTICE");

  const locale = useLocale();

  /* Toujours demandé ici : c'est l'écran de l'étape, et un organisateur qui l'ouvre
     après l'avoir retirée du parcours doit voir son catalogue, pas un blanc. */
  const { articles, saved, loading, failure } = useArticles(
    true,
    locale,
  );

  /**
   * ⚠️ UN ÉTAT LOCAL DEVANT LES DEUX AUTRES SOURCES, exactement comme à l'étape dates
   * et pour la même raison : tout décocher doit rester « rien », et non retomber sur la
   * sélection enregistrée — sinon les cases se recochent sous la main du participant.
   * Le `null` distingue « pas encore touché » de « touché, et vide ».
   */
  const [picked, setPicked] = useState<Selection | null>(null);
  const selection: Selection =
    picked ??
    answers.articles ??
    draftSelection(draft, saved, articles) ??
    saved ??
    {};

  /**
   * LES JOURS OÙ CETTE PERSONNE VIENT — la MÊME fonction que la confirmation, et pas
   * une seconde lecture des mêmes champs.
   *
   * ⚠️ UNE LISTE VIDE VAUT « PAS DE RÉPONSE », ET NON « AUCUN JOUR ». Écrite à la main,
   * la règle m'a échappé et l'écran l'a montré tout de suite : le participant de test
   * n'a aucune ligne dans `UserEventDays`, donc le filtre retirait TOUS les jours et il
   * ne restait que le niveau « hors jour ». C'est exactement le piège que
   * `funnel/dates.ts` documente — d'où l'appel à sa fonction plutôt qu'une condition de
   * plus, qui aurait pu diverger de ce que la confirmation inscrit ensuite.
   */
  const selected = selectedDaysOf({
    // Sur cet écran, la question ne se pose pas : on n'achète pas un billet pour un
    // événement auquel on a dit ne pas venir, et le fil ne mène pas ici dans ce cas.
    coming: true,
    ...(answers.days ? { chosen: answers.days } : {}),
    ...(recorded?.days ? { saved: recorded.days } : {}),
    all: days.map((d) => d.id),
  });
  const selectedDays = days
    .filter((d) => selected.includes(d.id))
    .map((d) => ({ id: d.id, label: longDay(d.day, locale) }));

  /* ⚠️ L'ÉTALAGE CONNAÎT CE QU'ON A DÉJÀ, pas seulement ce qui est en vente : un article
     pré-sélectionné au back office, ou acheté puis masqué depuis, doit rester lisible —
     sinon on paie une ligne qui n'apparaît nulle part. Voir `etalage`. */
  const stalls = salesDays(articles, selectedDays, selection);
  /* Le prix demandé est celui de ce qui sera ÉCRIT : un article d'un jour décoché est
     retiré à l'écriture (`cleanSelection`), il ne doit pas être chiffré ici. */
  const ids = flatIds(
    cleanSelection(selection, { selectedDays: selected, known: articles }),
    articles,
  );

  /**
   * ⚠️ UNE SÉLECTION VIDE SE DEMANDE, ELLE NE SE TAIT PLUS. Le serveur ne savait pas
   * distinguer « pas encore choisi » de « vidé exprès » : les deux s'écrivaient sans
   * paramètre, et le total affiché sous un panier vide redevenait celui de la commande
   * ENREGISTRÉE. Il sait maintenant, et cet écran demande donc toujours le prix de CE
   * qu'il montre — remboursement compris quand on retire ce qui a déjà été facturé.
   */
  const { resume } = useResume(ids, true);
  /* Le code promo suit le paiement, comme dans la V1 (`FO_MEETING_CONFIRMATION_HIDE_PAY`
     y masquait aussi son lien) : un organisateur qui encaisse autrement ne l'offre pas. */
  const payShown = useFlagOr("FO_MEETING_V2_PAY_SHOW");

  const { previous, next, go, unsaved } = useFunnelNav(ICI);
  const { ready: saveReady, state, save, touched, removal } = useStepSave({ asPage });

  const navigate = useNavigate();
  const event = useEvent();
  /**
   * ═══ EN PAGE, LE RÈGLEMENT EST À LA CONFIRMATION, ET L'ÉCRAN Y MÈNE (bug #412) ═══
   *
   * « Enregistrer » ne quitte pas la page (voir `useStepSave`) et il n'y a pas de dernière
   * étape où aller : la dette affichée n'avait aucune suite. Le paiement ne vit que sur la
   * confirmation (voir `funnel/payment.ts`), qui facture ce qui est ENREGISTRÉ.
   *
   * ⚠️ D'OÙ « RIEN EN ATTENTE », PAS SEULEMENT « RESTE À RÉGLER ». Le récapitulatif chiffre
   * la sélection À L'ÉCRAN : tant qu'elle diffère de la commande en base, la confirmation
   * réclamerait un autre montant que celui qu'on lit ici. La règle est celle du garde de
   * sortie (`unsaved`), qui avertit dans ce même cas.
   */
  const toCheckout = asPage && resume?.toPay === true && !unsaved;

  const clique = (day: SalesDay, articleId: number) => {
    const next = toggle(selection, day, articleId);
    setPicked(next);
    // « C'est enregistré » ne doit pas rester affiché au-dessus d'un panier qu'on vient
    // de modifier depuis.
    touched();
    // Enregistré dans CETTE entrée d'historique, comme les autres choix du tunnel :
    // sans quoi le bouton Retour du navigateur rendrait l'étape vierge.
    record({ articles: next });
    keep({ articles: next });
  };

  /**
   * ⚠️ « OBLIGATOIRE » SE JUGE PAR JOUR PROPOSÉ, pas sur l'ensemble. C'est la règle de
   * la V1, dont la contrainte porte sur chaque `Form.Item` — un par jour. Exiger « au
   * moins un article » globalement laisserait passer quelqu'un qui a pris le pass et
   * sauté le déjeuner du mardi, alors que l'organisateur qui coche cette case demande
   * une réponse pour chaque journée.
   *
   * ⚠️ LA RÈGLE VIT DANS `eachDayChosen`, et le fil d'Ariane pose la même : sinon on le
   * prenait pour sauter l'étape (voir `funnel/Steps.tsx`).
   */
  const full = eachDayChosen(stalls, selection);
  /* Tant que la lecture de l'existant n'est pas RÉGLÉE (en vol, ou en erreur), on ne
     montre pas la liste cliquable : un clic figerait `touche` sur une base vide et
     écraserait, à la confirmation, la commande déjà enregistrée. Le spinner ou le message
     d'erreur tient l'écran à sa place. */
  const listReady = !loading && !failure;
  const empty = listReady && stalls.length === 0;

  return (
    <section className="page">
      <div className="ttl" data-cfg="articlesTitle">
        <InPlace settingKey="FO_MEETING_V2_ARTICLES_TITLE" text={title} as="h2" />
        <InPlace settingKey="FO_MEETING_V2_ARTICLES_SUBTITLE" text={subtitle} as="p" />
      </div>

      {html.trim() !== "" && (
        <Prose
          html={html}
          className="se-prose"
          data-cfg="articlesTitle"
          />
      )}

      {failure && (
        <p className="loginerr" role="alert">
          {t(
            "TICKETING_NOT_LOADED",
            "La billetterie n'a pas pu être chargée. Rechargez la page, et prévenez l'organisateur si cela persiste.",
          )}
        </p>
      )}

      {loading && (
        <p className="empty">
          <span className="spin" aria-hidden="true" />
          <span className="a11y" role="status">
            {t("LOADING_TICKETING", 'Chargement de la billetterie')}
          </span>
        </p>
      )}

      {/* ⚠️ LA LISTE VIDE A TROIS CAUSES, et c'est l'ORGANISATEUR qui lit ce message —
          un participant n'arrive pas ici sur un événement qui ne vend rien. Les
          distinguer lui évite de chercher la panne dans le mauvais écran : le
          catalogue peut être vide, les articles peuvent n'être ouverts sur AUCUN jour
          (c'est le cas le plus courant : `days` vaut `{}` sur seize des vingt articles
          de l'événement de test), ou n'être ouverts que sur des jours que ce
          participant n'a pas retenus. */}
      <div className="alert" data-cfg="articlesTitle" hidden={!empty}>
        <span className="alert__d" aria-hidden="true">
          <i />
        </span>
        <span>
          <b>Rien à proposer ici</b>
          <small>
            Soit l'événement ne déclare aucun article au back office, soit aucun
            n'est ouvert à la vente ni retenu pour vous. Un article se propose
            aux jours où le back office l'a coché « visible » ; s'il est
            seulement pré-sélectionné, il s'affiche quand même, sans pouvoir
            être retiré.
          </small>
        </span>
      </div>

      {/* Ancrée sur la famille de l'étape faute d'en avoir une à elle : le catalogue
          vient du back office, aucun réglage V2 ne le gouverne. Même raison qu'aux
          étapes dates et conférences — sans `data-cfg`, le bloc sortirait de la trappe
          à CSS. */}
      {listReady && (
      <div className="arts" data-cfg="articlesTitle">
        {stalls.map((day) => (
          <div className="artday" key={day.id}>
            {/* ⚠️ PAS D'EN-TÊTE POUR LE NIVEAU « HORS JOUR », comme la V1 qui n'en met
                aucun (`label={day ? getDate(day) : null}`). Ce groupe-là n'est pas une
                journée : c'est ce qui se vend pour l'événement entier. */}
            {day.label && (
              <div className="artday__h">
                <b>{day.label}</b>
                {day.singleChoice && <span>{t("ONE_CHOICE_ONLY", 'Un seul choix')}</span>}
              </div>
            )}

            {day.articles.map((article) => {
              const on = isSelected(selection, day.id, article.id);
              /* Verrouillé par le back office, OU affiché parce qu'on l'a sans qu'il
                 soit en vente : dans les deux cas la ligne ne se décoche pas. */
              const closed = locked(article, day.id);
              return (
                <button
                  type="button"
                  key={article.id}
                  className={`art${on ? " on" : ""}${closed ? " off" : ""}`}
                  /* Un interrupteur, et il s'annonce comme tel — y compris en choix
                     unique, où recliquer rend l'article (voir `basculer`). */
                  aria-pressed={on}
                  /* `aria-disabled` et non `disabled` : la ligne reste atteignable au
                     clavier et lisible par une synthèse vocale, ce qui est le seul
                     moyen d'apprendre POURQUOI elle ne répond pas. Même arbitrage que
                     les conférences. */
                  aria-disabled={closed}
                  onClick={() => !closed && clique(day, article.id)}
                >
                  {/* ⚠️ LE MARQUEUR SUIT CE QUE LE JOUR AUTORISE, et c'est le seul
                      endroit du tunnel où il change d'une liste à l'autre : le back
                      office peut n'ouvrir qu'un article pour une journée
                      (`days[jour].isRadio`), et c'est alors un choix exclusif. Un carré
                      y promettrait qu'on peut en cocher deux — ce que le clic suivant
                      démentirait en remplaçant le premier. */}
                  <span
                    className={day.singleChoice ? "opt__r" : "opt__c"}
                    aria-hidden="true"
                  >
                    {on && <Icon name="check" />}
                  </span>
                  {/* ⚠️ CIBLÉ PAR SA CLASSE, ET NON PAR `.art > span`. Le prototype
                      étire son premier `span` par la position ; avec une case devant,
                      c'est la case qui se serait étirée — l'erreur exacte déjà commise
                      sur `.ctc__h > span`, qui avait étalé un avatar. */}
                  <span className="art__t">
                    <b>{article.label}</b>
                    <ArticlePrice article={article} />
                  </span>
                </button>
              );
            })}
          </div>
        ))}
      </div>
      )}

      {/* ⚠️ LE RÉCAPITULATIF NE S'AFFICHE QUE S'IL Y A QUELQUE CHOSE À DIRE. Une
          facture à zéro sous une liste vide n'apprend rien, et sur l'écran qui engage
          de l'argent le silence vaut mieux qu'un tableau de zéros. */}
      {resume && (resume.subtotalExclTax !== 0 || resume.alreadyPaidInclTax !== 0) && (
        <div className="recap" data-cfg="articlesRecap">
          <div className="rsec">{t("YOUR_ORDER", 'Votre commande')}</div>

          <div className="rrow">
            <span>{t("SUBTOTAL", 'Sous-total')}</span>
            <b>{t("AMOUNT_EXCL_TAX", "$1 HT", [euros(resume.subtotalExclTax, locale)])}</b>
          </div>

          {/* ⚠️ CHAQUE LIGNE DIT SON RÉGIME. Sans « HT » et « TTC » écrits, le tableau
              montrait « − 169,00 € » puis « 202,80 € » pour le même argent (relevé par
              William le 2026-09-11) : le premier hors taxes, le second avec, et rien
              pour le dire. Le décompte se tient en hors-taxes jusqu'à la TVA, puis passe
              en toutes taxes — c'est dans ce régime-là qu'on paie et qu'on rembourse. */}
          {resume.freeExclTax > 0 && (
            <div className="rrow">
              <span>{t("FREE_OF_CHARGE", 'Offert')}</span>
              <b>− {t("AMOUNT_EXCL_TAX", "$1 HT", [euros(resume.freeExclTax, locale)])}</b>
            </div>
          )}

          {resume.discountExclTax > 0 && (
            <div className="rrow">
              <span>{t("DISCOUNT", 'Remise')}</span>
              <b>− {t("AMOUNT_EXCL_TAX", "$1 HT", [euros(resume.discountExclTax, locale)])}</b>
            </div>
          )}

          {resume.vatLines.map((line) => (
            <div className="rrow" key={line.rate}>
              <span>{t("VAT_RATE", "TVA $1 %", [line.rate])}</span>
              <b>{euros(line.amount, locale)}</b>
            </div>
          ))}

          {/* Le grief le plus dur de la revue par personas : ne pas redemander ce qui
              a déjà été payé. APRÈS la TVA, et en toutes taxes : c'est la somme que la
              personne a réellement sortie, et celle qu'on lui rendra. */}
          {resume.alreadyPaidInclTax > 0 && (
            <div className="rrow">
              <span>{t("ALREADY_PAID", 'Déjà réglé')}</span>
              <b>− {t("AMOUNT_INCL_TAX", "$1 TTC", [euros(resume.alreadyPaidInclTax, locale)])}</b>
            </div>
          )}

          {/**
           * ⚠️ UN TROP-PERÇU NE DISPARAÎT PAS EN SILENCE. Retirer un article après
           * paiement rend un total NÉGATIF — le serveur le donne tel quel (mesuré :
           * −431 € sur l'événement de test). L'afficher comme un « reste à régler »
           * négatif serait absurde ; le taire ferait croire à une perte.
           */}
          {resume.remainingInclTax < 0 ? (
            <div className="rrow">
              <span>{t("OVERPAID_CREDIT_NOTE", 'Trop-perçu · un avoir vous sera fait')}</span>
              <b>{t("AMOUNT_INCL_TAX", "$1 TTC", [euros(-resume.remainingInclTax, locale)])}</b>
            </div>
          ) : (
            <div className="rrow">
              <span>
                {resume.toPay
                  ? t("LEFT_TO_PAY", "Reste à régler")
                  : t("NOTHING_TO_PAY", "Rien à régler")}
              </span>
              <b>{t("AMOUNT_INCL_TAX", "$1 TTC", [euros(resume.remainingInclTax, locale)])}</b>
            </div>
          )}
        </div>
      )}

      {/* ⚠️ SOUS LE RÉCAPITULATIF, ET SEULEMENT S'IL Y A QUELQUE CHOSE À RÉDUIRE (ou un
          code déjà posé, qu'on doit pouvoir lire même s'il a tout couvert) : c'est la
          règle de la V1, dont le lien vivait dans le récapitulatif et disparaissait
          avec lui quand rien n'était dû. Voir `funnel/PromoCode.tsx`. */}
      {payShown &&
        resume &&
        (resume.subtotalExclTax !== 0 || resume.alreadyPaidInclTax !== 0) &&
        (resume.toPay || resume.promoCode) && (
          <div data-cfg="articlesRecap">
            <PromoCode resume={resume} />
          </div>
        )}

      {/**
       * ⚠️ ANNONCER UNE DETTE SANS DIRE OÙ LA RÉGLER EST UN CUL-DE-SAC. Cet écran
       * affiche un « reste à régler » et n'a pas de bouton de paiement : le règlement
       * ne peut avoir lieu qu'à la CONFIRMATION, parce que le serveur facture ce qui
       * est ENREGISTRÉ et que la commande ne l'est qu'à la fin du parcours (voir
       * `funnel/payment.ts`). Sans cette phrase, on lit un montant dû, on cherche un
       * bouton, on n'en trouve pas — et rien à l'écran ne dit qu'il faut continuer.
       *
       * ⚠️ ELLE NE PROMET PAS UN PAIEMENT EN LIGNE : l'organisateur peut l'avoir
       * retiré, auquel cas il encaisse autrement. Elle dit où l'on va, pas ce qu'on y
       * trouvera.
       */}
      {/* En page, il n'y a pas de dernière étape : c'est le bouton plus bas qui y mène. */}
      {!asPage && resume?.toPay && (
        <p className="hint" data-cfg="articlesRecap">
          {t("PAYMENT_AT_LAST_STEP", 'Le règlement se fait à la dernière étape, une fois votre inscription enregistrée.')}
        </p>
      )}

      {mentions.trim() !== "" && (
        <p className="hint" data-cfg="articlesNotice">
          {mentions}
        </p>
      )}

      <SaveNote state={state} removal={removal} />

      {/* Dans le tunnel « Continuer », en page « Enregistrer », jamais les deux. Voir
          l'étape dates, où la règle est écrite. */}
      <div className="actions">
        {asPage && (
          <Button type="button" variant="ghost" onClick={() => go("/")}>
            {t("BACK_TO_HOME", "Retour à l'accueil")}
          </Button>
        )}
        {!asPage && previous && (
          <Button
            type="button"
            variant="ghost"
            onClick={() => go(previous.path)}
          >
            {t("BACK", "Retour")}
          </Button>
        )}
        {asPage && (
          <Button
            type="button"
            /* Secondaire quand le chemin du règlement est là ; seul, principal. */
            variant={toCheckout ? "ghost" : undefined}
            loading={state === "saving"}
            /* ⚠️ ET PAS TANT QUE L'EXISTANT N'EST PAS LU (`pret`). Cet écran refuse déjà
               le clic dans ce cas : sans la même garde ici, « Enregistrer » écrirait la
               sélection VIDE qui tient lieu de repli, et effacerait une commande déjà
               réglée. */
            disabled={!saveReady || !listReady || (required && !full)}
            onClick={() => save({ articles: selection })}
          >
            {t("SAVE", "Enregistrer")}
          </Button>
        )}
        {toCheckout && (
          <Button
            type="button"
            /**
             * ⚠️ SANS LES RÉPONSES, comme « Enregistrer » dans le tunnel : la confirmation
             * écrit ce que l'état de navigation porte dès son arrivée, et y emporter la
             * commande la détruirait puis la recréerait, lignes réglées comprises. Vide,
             * elle relit et propose le règlement.
             *
             * ⚠️ LE LIBELLÉ NE PROMET PAS UN PAIEMENT EN LIGNE : l'organisateur peut l'avoir
             * retiré, et la confirmation dit alors comment il encaisse.
             */
            onClick={() =>
              navigate(withContext(CONFIRMATION, event?.token), { state: {} })
            }
          >
            {t("SEE_HOW_TO_PAY_ORDER", "Voir comment régler ma commande")}
          </Button>
        )}
        {!asPage && (
          <Button
            type="button"
            /* ⚠️ ET PAS TANT QUE L'EXISTANT N'EST PAS LU : en échec de lecture la sélection
               vaut `{}`, et « Continuer » l'aurait envoyée, ce qui détruit la commande
               enregistrée (payée comprise). Même garde que « Enregistrer ». */
            disabled={!next || !listReady || (required && !full)}
            onClick={() => next && go(next.path, { articles: selection })}
          >
            {t("CONTINUE", "Continuer")}
          </Button>
        )}
      </div>
    </section>
  );
}
